import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

// src/app/core/helpers/utils.ts
export const CURRENCY_SYMBOL = '$';

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validateEmail(email: string): boolean {

  if (email.trim().length > 0 && !isValidEmail(email)) {
    displayErrorMessage('email', 'Please enter a valid email.');
    return false;
  }
  hideErrorMessage('email');
  return true;
}

export function truncateText(text: string, limit: number = 50): string {
  if (!text) return '';

  const trimmed = text.trim();

  return trimmed.length > limit
    ? trimmed.substring(0, limit) + '...'
    : trimmed;
}
export function displayErrorMessage(key: string, message: string) {
  const elements = document.querySelectorAll<HTMLElement>(`.${key}`);

  if (key === 'error') {
    elements.forEach(el => {
      el.textContent = message;
      el.classList.remove('d-none');
    });
  } else {

    elements.forEach(el => {
      el.classList.remove('d-none');
    });

    const fieldElements = document.querySelectorAll<HTMLElement>(`.${key} > span`);
    fieldElements.forEach(el => {
      el.textContent = message;
    });

  }
}
export function hideErrorMessage(key: string) {
  const elements = document.querySelectorAll<HTMLElement>(`.${key}`);

  elements.forEach(el => {
    el.classList.add('d-none');
  });

}

export function getErrorMessage(key: string, errors: any[]): string | null {
  if (!Array.isArray(errors)) return null;

  const found = errors.find(err => err.hasOwnProperty(key));

  return found ? found[key] : null;
}

export function apiErrorMessage(err: any, fallback: string): string {
  const first = err?.error?.errors?.[0];
  if (!first) return fallback;
  return first.error ?? Object.values(first)[0] ?? fallback;
}

export function formatCurrency(amount: any) {
  const num = Number(amount) || 0;
  return CURRENCY_SYMBOL + num.toFixed(2);
}
export function getInitials(fullName: string): string {

  const words = fullName.trim().split(/\s+/).filter(word => word.trim().length > 0);

  if (words.length === 1) {
    return words[0].slice(0, 1).toUpperCase();
  }
  return words[0].slice(0, 1).toUpperCase() + words[1].slice(0, 1).toUpperCase();
}
export function buildQueryString(params?: Record<string, string | number>): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => usp.append(key, String(value)));
  return `?${usp.toString()}`;
}

export const ORIGINATING_SOURCES = [
  'ACA',
  'UnifyWell',
  'JV',
] as const;
export const ORIGINATING_SOURCES_DROPDOWN = [
  { label: 'Select', value: '' },
  ...ORIGINATING_SOURCES.map(source => ({
    label: source,
    value: source,
  })),
];
export function decodeHtmlEntities(value: string | null | undefined): string {
  if (!value) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(value, 'text/html');
  return doc.documentElement.textContent ?? '';
}
export function safeParse(value: any): Record<string, string> {
  try {
    return typeof value === 'string' ? JSON.parse(value) : value ?? {};
  } catch {
    return {};
  }
};

function isEmpty(v: any): boolean {
  return v === null || v === undefined || v === '';
}

export function checkBankReEntry(
  routingNo: any,
  bankNo: any,
  originalRoutingNo: any,
  originalBankNo: any,
  reRoutingNo: any,
  reBankNo: any,
): { showModal: boolean, mismatchMessage: string,} {
  const routingChanged = routingNo != originalRoutingNo;
  const bankChanged = bankNo != originalBankNo;

  // Nothing changed — proceed, no modal needed at all
  if (!routingChanged && !bankChanged) {
    return { showModal: false, mismatchMessage: '' };
  }

  // Always validate re-entry for the field(s),
  // and only flag mismatch once the user has actually typed something.
  const routingMismatch = !isEmpty(reRoutingNo) && routingNo != reRoutingNo;
  const bankMismatch = !isEmpty(reBankNo) && bankNo != reBankNo;

  if (routingMismatch) {
    return { showModal: true, mismatchMessage: 'Routing Number does not match.' };
  }
  if (bankMismatch) {
    return { showModal: true, mismatchMessage: 'Account Number does not match.' };
  }

  // Something changed, but re-entry either matches or hasn't been filled yet.
  // If re-entry is still empty for the changed field(s), keep the modal open silently.
  const routingPending = isEmpty(reRoutingNo) && routingNo != reRoutingNo;
  const bankPending = isEmpty(reBankNo) && bankNo != reBankNo;

  if (routingPending || bankPending) {
    return { showModal: true, mismatchMessage: '' }; // waiting on user input, no error yet
  }

  // Everything changed field re-entered correctly
  return { showModal: false, mismatchMessage: '' };
}
// Employer flow: Created (default) -> OnBoarding -> Live -> Terminated
export const EMPLOYER_DEFAULT_STATUS = 'created';
export const EMPLOYER_STATUS_LABELS: Record<string, string> = {
  'created': 'Created',
  'onboarding': 'Onboarding',
  'live': 'Live',
  'terminated': 'Terminated',
};

/** Lowercase employer overall_status; a missing status is the default created. */
export function normalizeEmployerStatus(status: any): string {
  return String(status || EMPLOYER_DEFAULT_STATUS).toLowerCase();
}

/** Display label for an employer overall_status; a missing status is the default Created. */
export function employerStatusLabel(status: any): string {
  const key = normalizeEmployerStatus(status);
  return EMPLOYER_STATUS_LABELS[key] ?? key;
}

export const EMPLOYEE_STATUS_LABELS: Record<string, string> = {
  'opt-in': 'Opt-In',
  'opt-out': 'Opt-Out',
  'opted-out': 'Opted-Out',
  'enrolled': 'Enrolled',
  'excluded': 'Excluded',
  'terminated': 'Terminated',
  'not eligible': 'Not Eligible',
};

/**
 * Display label for an employee status. Ineligible employees always read
 * "Not Eligible" regardless of the status stored against them.
 */
export function employeeStatusLabel(status: any, isEligible: any = 1): string {
  if (!isEligible) return 'Not Eligible';

  const key = String(status ?? '').trim().toLowerCase();
  if (!key) return '';

  return (
    EMPLOYEE_STATUS_LABELS[key] ||
    key.replace(/[a-z]+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1))
  );
}

export function bothOrNoneValidator(
  fieldA: string,
  fieldB: string,
  errorKey: string = 'bothOrNone'
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const controlA = group.get(fieldA);
    const controlB = group.get(fieldB);

    // Defensive: if either field doesn't exist, don't fail — just skip
    if (!controlA || !controlB) return null;

    const hasA = String(controlA.value ?? '').trim().length > 0;
    const hasB = String(controlB.value ?? '').trim().length > 0;

    // Both empty or both filled = valid at the pair level
    if (hasA === hasB) return null;

    // One filled, one empty = invalid
    return {
      [errorKey]: {
        missingField: hasA ? fieldB : fieldA,
        fields: [fieldA, fieldB],
      },
    };
  };
}
